import React, { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import { F0Button } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import Delete from '@factorialco/f0-react/icons/app/Delete'

import i18n from 'modules/core/lib/i18n'

import FromField from './FromField'
import StopField from './StopField'
import ToField from './ToField'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'

import { useMileageDistanceCalculation } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/mileage/Trip/useMileageDistanceCalculation'

interface Props extends PermissionsProps, CustomizationProps {
  setIsCalculatingDistance?: (loading: boolean) => void
  hasMileageMultipleStops?: boolean
}

interface Place {
  lat: number
  lng: number
}

interface StopFormValue {
  name?: string | null
  latitude?: string | null
  longitude?: string | null
}

const Trip = (props: Props) => {
  const { setValue, watch } = useFormContext()
  const { setIsCalculatingDistance, hasMileageMultipleStops = false } = props

  const hasExistingData = Boolean(props.expensable?.id)

  const { stops } = useMileageDistanceCalculation({
    setIsCalculatingDistance,
    hasExistingData,
  })

  const handleFromPlaceSelected = useCallback(
    (place: Place) => {
      setValue('originLatitude', String(place.lat), { shouldValidate: true, shouldDirty: true })
      setValue('originLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleToPlaceSelected = useCallback(
    (place: Place) => {
      setValue('destinationLatitude', String(place.lat), {
        shouldValidate: true,
        shouldDirty: true
      })
      setValue('destinationLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleStopPlaceSelected = useCallback(
    (index: number, place: Place) => {
      setValue(`stops.${index}.latitude`, String(place.lat), {
        shouldValidate: true,
        shouldDirty: true
      })
      setValue(`stops.${index}.longitude`, String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleFromInputChange = useCallback(() => {
    setValue('originLatitude', '', { shouldValidate: true })
    setValue('originLongitude', '', { shouldValidate: true })
  }, [setValue])

  const handleToInputChange = useCallback(() => {
    setValue('destinationLatitude', '', { shouldValidate: true })
    setValue('destinationLongitude', '', { shouldValidate: true })
  }, [setValue])

  const handleStopInputChange = useCallback(
    (index: number) => {
      setValue(`stops.${index}.latitude`, '', { shouldValidate: true })
      setValue(`stops.${index}.longitude`, '', { shouldValidate: true })
    },
    [setValue]
  )

  const handleAddStop = useCallback(() => {
    const current: StopFormValue[] = watch('stops') || []
    const next = [...current, { name: '', latitude: null, longitude: null }]
    setValue('stops', next, { shouldDirty: true })
  }, [watch, setValue])

  const handleRemoveStop = useCallback(
    (index: number) => {
      const current: StopFormValue[] = watch('stops') || []
      const next = current.filter((_, i) => i !== index)
      setValue('stops', next, { shouldDirty: true })
    },
    [watch, setValue]
  )

  return (
    <Stack gap="4">
      <FromField
        {...props}
        fieldIdentifier="from"
        onPlaceSelected={handleFromPlaceSelected}
        onInputChange={handleFromInputChange}
      />

      {hasMileageMultipleStops &&
        stops.map((_stop: StopFormValue, index: number) => (
          <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <StopField
                {...props}
                fieldIdentifier="stops"
                index={index}
                onPlaceSelected={(place: Place) => handleStopPlaceSelected(index, place)}
                onInputChange={() => handleStopInputChange(index)}
              />
            </div>
            <F0Button
              variant="outline"
              icon={Delete}
              label={i18n.t('expenses_v2.forms.fields.stop.remove')}
              hideLabel
              onClick={() => handleRemoveStop(index)}
            />
          </div>
        ))}

      {hasMileageMultipleStops && (
        <div>
          <F0Button
            variant="outline"
            label={i18n.t('expenses_v2.forms.fields.stop.add')}
            onClick={handleAddStop}
          />
        </div>
      )}

      <ToField
        {...props}
        fieldIdentifier="to"
        onPlaceSelected={handleToPlaceSelected}
        onInputChange={handleToInputChange}
      />
    </Stack>
  )
}

export default Trip
